/**
 * Browser-local preferences for Question Bank Migration and Batch Question Bank Migration
 * pre-save / duplicate-guard modals. Managed from Account → Preferences.
 */

const SOFT_DISABLE_KEY = 'webadmin:questionBankMigration:disableSoftPreSaveModal';
const HARD_DISABLE_KEY = 'webadmin:questionBankMigration:disableHardDuplicateModal';

/** When true, skip the informational pre-save summary modal (soft block). Default false = show. */
export function readDisableSoftPreSaveModal(): boolean {
  try {
    return localStorage.getItem(SOFT_DISABLE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDisableSoftPreSaveModal(value: boolean): void {
  try {
    localStorage.setItem(SOFT_DISABLE_KEY, value ? '1' : '0');
  } catch {
    /* ignore private mode / quota */
  }
}

/** When true, skip similar / duplicate-id guard modals and checks (hard block). Default false = show. */
export function readDisableHardDuplicateModal(): boolean {
  try {
    return localStorage.getItem(HARD_DISABLE_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDisableHardDuplicateModal(value: boolean): void {
  try {
    localStorage.setItem(HARD_DISABLE_KEY, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/**
 * Skip read-only “before Firebase write” review UIs: Question Bank Migration pre-save summary, and Batch
 * “Review IDs before saving to Firebase”. True when **either** soft pre-save is disabled **or** the hard duplicate
 * guard is disabled (streamlined runs often turn both off; turning hard off alone also skips the review).
 */
export function readSkipQuestionBankFirebaseReviewModal(): boolean {
  return readDisableSoftPreSaveModal() || readDisableHardDuplicateModal();
}

/**
 * Batch: after state is saved, if the common write hits an id collision, auto-allocate a new common id and retry
 * (instead of `needs_manual_save`) when the operator did not opt into “soft pre-save only, no auto-bump” — i.e. when
 * soft pre-save is **on** (user can confirm in the review modal) **or** hard duplicate guard is **off** (streamlined
 * save path).
 */
export function readBatchAutoBumpCommonOnFirestoreCollision(): boolean {
  return !readDisableSoftPreSaveModal() || readDisableHardDuplicateModal();
}

const BATCH_SKIP_FIREBASE_REVIEW_KEY = 'webadmin:batchQuestionBankMigration:skipFirebaseSaveReviewModal';

/** Batch tab only: skip the per-question “Review IDs before saving to Firebase” modal. Default false = show. */
export function readBatchOnlySkipFirebaseSaveReviewModal(): boolean {
  try {
    return localStorage.getItem(BATCH_SKIP_FIREBASE_REVIEW_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeBatchOnlySkipFirebaseSaveReviewModal(value: boolean): void {
  try {
    localStorage.setItem(BATCH_SKIP_FIREBASE_REVIEW_KEY, value ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** When true, batch writes skip the read-only ID review modal (Account prefs or batch-only checkbox). */
export function readEffectiveSkipBatchFirebaseSaveReviewModal(): boolean {
  return readSkipQuestionBankFirebaseReviewModal() || readBatchOnlySkipFirebaseSaveReviewModal();
}
